import { createTheme } from '@mui/material'

// Brand Colors (from brand palette)
export const TEAL = '#038CA5' // primary
export const YELLOW = '#FFDE59' // secondary / accent
export const MINT = '#CCFED8' // soft accent background
export const BLUE = '#96BCFD' // soft accent

// Backwards-compatible aliases (older code referenced NAVY / GOLD)
export const NAVY = TEAL
export const GOLD = YELLOW

// Casual flat-brush connected script for headline sections — matches the
// brand's t-shirt lettering (rounded, slightly-slanted brush handwriting).
export const DISPLAY_FONT = '"Yellowtail", "Inter", cursive'

export const theme = createTheme({
  palette: {
    primary: {
      main: TEAL,
      contrastText: '#fff',
    },
    secondary: {
      main: YELLOW,
      contrastText: '#0B3A44',
    },
    background: {
      default: '#F6FBFC',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // Casual brush-script display font for headline sections. Yellowtail has
    // tall ascenders, so it still wants a little breathing room in line-height.
    h1: {
      fontFamily: DISPLAY_FONT,
      fontWeight: 400,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: DISPLAY_FONT,
      fontWeight: 400,
      lineHeight: 1.25,
    },
    h3: {
      fontFamily: DISPLAY_FONT,
      fontWeight: 400,
      lineHeight: 1.25,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '10px 24px',
        },
        containedSecondary: {
          color: '#0B3A44',
          '&:hover': {
            backgroundColor: '#F2CB2E',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
      },
    },
  },
})

export const PHONE_DISPLAY = '(925) 384-9602'
export const PHONE_HREF = 'tel:+19253849602'

export const IMAGES = {
  logo: 'https://cdn.magicpatterns.com/uploads/wnjiAdgYZ7A9cZvwY9rBvd/The_Patio_Cover_Company_logo_1.png',
  hero: 'https://cdn.magicpatterns.com/uploads/bJdbYsa29RntEG1d1Qaemj/713423704_17990473709989871_522829978471671744_n.jpg',
  polycarbonate:
    'https://cdn.magicpatterns.com/uploads/pzcViMzsNsxJ7eRtPkzyf5/712429354_17990473529989871_8682494510618591964_n.jpg',
  solid:
    'https://cdn.magicpatterns.com/uploads/8GZC7WmDMZQDTqtMLAFSCP/715494669_17990473604989871_1650862984105553916_n.jpg',
  patioFan:
    'https://cdn.magicpatterns.com/uploads/9rwys7LHuL2BkH3VFSo4WT/Screenshot_2026-06-18_at_5.44.00_PM.png',
  waterfront:
    'https://cdn.magicpatterns.com/uploads/ipdh5njC1TaeHihHqVX8j1/Screenshot_2026-06-18_at_5.44.21_PM.png',
  hillsideGarden:
    'https://cdn.magicpatterns.com/uploads/17bgMrhXywaVTKXR5oqeYB/Screenshot_2026-06-18_at_5.44.31_PM.png',
  attachedSolid:
    'https://cdn.magicpatterns.com/uploads/83gyS52FZnt91HyB51JMeD/Screenshot_2026-06-18_at_5.44.39_PM.png',
  detailGutter:
    'https://cdn.magicpatterns.com/uploads/ds5GcTrdmEnUevqCW9ge3F/Screenshot_2026-06-18_at_5.44.50_PM.png',
  loungeGarden:
    'https://cdn.magicpatterns.com/uploads/vWL4DBorfiNwy35wQCiZky/683767875_17985693452989871_7970532378316909495_n.jpg',
  wraparound:
    'https://cdn.magicpatterns.com/uploads/jFCQXRPxUPKBaG1MyQo5FY/684945779_17986801436989871_977118997827246606_n.jpg',
  whiteCorner:
    'https://cdn.magicpatterns.com/uploads/rwxbeN8pVSFGoRpuqgUtwk/685412504_17985698249989871_7215094507049831814_n.jpg',
  shadeScreen:
    'https://cdn.magicpatterns.com/uploads/fdGCFK92wxYRsbGWQkiM5J/685636683_17986833230989871_8677179998515184832_n.jpg',
  deckCover:
    'https://cdn.magicpatterns.com/uploads/5MFmN9uYYeJGfRL8X2HuB4/689778053_17986801493989871_6268000523810328782_n.jpg',
  poolside:
    'https://cdn.magicpatterns.com/uploads/tx5TUEbasijgqCRKFG3EHw/659415803_17984143589989871_6688520859779240850_n.jpg',
  whiteFlat:
    'https://cdn.magicpatterns.com/uploads/8BEsLYqFzETPeJ7QHc61Jz/669883243_17983573994989871_6262184458219981307_n.jpg',
  poolLattice:
    'https://cdn.magicpatterns.com/uploads/3A4kGaJR99SEpRXf7SPuRL/671125538_17987904902989871_2987171214036689456_n.jpg',
  wraparoundGray:
    'https://cdn.magicpatterns.com/uploads/ixi471x2d5zAQRdWrW61yp/671793699_17987905364989871_7482113793182828669_n.jpg',
  graySolid:
    'https://cdn.magicpatterns.com/uploads/4Ve7hFqZxQSKMLXupWbgN8/672336937_17983812833989871_3766857262398901242_n.jpg',
  sunroom:
    'https://cdn.magicpatterns.com/uploads/8KsPvo6FV25dsN2Nj5UYKU/557348009_17961885518989871_4873558110008622868_n.jpg',
  freestanding:
    'https://cdn.magicpatterns.com/uploads/o2GkaKtnLs9VP6egX4Dknb/588443325_17969132861989871_6685850609960240796_n.jpg',
  recessedLighting:
    'https://cdn.magicpatterns.com/uploads/p7WDbJSiYaUP2oVarwZwWV/621745413_17973849893989871_6060720729533642607_n.jpg',
  deckBalcony:
    'https://cdn.magicpatterns.com/uploads/5RUtp2JL1rLQ27s8ixfoZi/652476051_17979550667989871_4850015428863899415_n.jpg',
  stringLights:
    'https://cdn.magicpatterns.com/uploads/kr7WFCt1b78iuw5Ntwq7C9/658761824_17982981674989871_8806963426675251837_n.jpg',
  stoneLattice:
    'https://cdn.magicpatterns.com/uploads/5fQL1N5mgiKhpZvZfA3CQB/520202008_17953277771989871_6942221501853547512_n.jpg',
  waterfrontDeck:
    'https://cdn.magicpatterns.com/uploads/1Kf1uvk22SMNcnVRZxTnmr/523453004_17954268365989871_5891980032178308592_n.jpg',
  hillsideView:
    'https://cdn.magicpatterns.com/uploads/2M4tZ47jpSErzxrnwwnRfA/524286725_17954268155989871_6049927520627914533_n.jpg',
  gardenSolid:
    'https://cdn.magicpatterns.com/uploads/f82rFZP4v3h6Xf6UjejeSq/528024056_17955531140989871_5446306041837885655_n.jpg',
  frontLattice:
    'https://cdn.magicpatterns.com/uploads/28PfGhQZXHgC7jBPsTRY9J/545081860_17959648361989871_1789044684591199351_n.jpg',
}

// Shared style for the yellow eyebrow / accent text. The yellow (#FFDE59) is
// low-contrast on light backgrounds, so we add a dark text outline + soft glow
// behind the letters to make it pop dramatically on both light and dark sections.
export const yellowGlowSx = {
  textShadow: '0 1px 6px rgba(0, 0, 0, 0.5), 0 2px 14px rgba(0, 0, 0, 0.35)',
} as const

export const scrollToQuote = () => {
  document.getElementById('quote-form')?.scrollIntoView({
    behavior: 'smooth',
  })
}
